import { Alert, Button, useToast } from '@cartly/ui';
import { MessageCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { api, errorMessage } from '../../api/client';

interface FacebookSdk {
  init(opts: { appId: string; autoLogAppEvents?: boolean; xfbml?: boolean; version: string }): void;
  login(
    cb: (response: { authResponse?: { code?: string; accessToken?: string } | null }) => void,
    opts: Record<string, unknown>,
  ): void;
}

declare global {
  interface Window {
    FB?: FacebookSdk;
    fbAsyncInit?: () => void;
  }
}

const SDK_URL = 'https://connect.facebook.net/en_US/sdk.js';
const META_ORIGINS = new Set([
  'https://www.facebook.com',
  'https://web.facebook.com',
  'https://business.facebook.com',
]);

let sdkLoaded: Promise<FacebookSdk> | null = null;

export function loadSdk(appId: string, version: string): Promise<FacebookSdk> {
  sdkLoaded ??= new Promise<FacebookSdk>((resolve, reject) => {
    window.fbAsyncInit = () => {
      window.FB!.init({ appId, autoLogAppEvents: true, xfbml: false, version });
      resolve(window.FB!);
    };
    const script = document.createElement('script');
    script.src = SDK_URL;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.onerror = () => {
      sdkLoaded = null;
      reject(new Error('Could not load the Facebook SDK. Check for a content blocker.'));
    };
    document.head.appendChild(script);
  });
  return sdkLoaded;
}

interface SignupSession {
  wabaId: string;
}

const FINISH_EVENTS = new Set([
  'FINISH',
  'FINISH_ONLY_WABA',
  'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING',
]);

/** Meta's hosted Embedded Signup: popup → session event (WABA id) + code → server exchange. */
export function EmbeddedSignup({
  appId,
  configId,
  solutionId,
  graphVersion,
  onConnected,
}: {
  appId: string;
  configId: string;
  solutionId: string | null;
  graphVersion: string;
  onConnected: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const session = useRef<SignupSession | null>(null);
  const cancelled = useRef(false);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (!META_ORIGINS.has(event.origin) || typeof event.data !== 'string') return;
      try {
        const msg = JSON.parse(event.data) as {
          type?: string;
          event?: string;
          data?: { waba_id?: string };
        };
        if (msg.type !== 'WA_EMBEDDED_SIGNUP') return;
        if (msg.event && FINISH_EVENTS.has(msg.event) && msg.data?.waba_id) {
          session.current = { wabaId: msg.data.waba_id };
        } else if (msg.event === 'CANCEL') {
          cancelled.current = true;
        }
      } catch {
        /* not a JSON message */
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const waitForSession = async (): Promise<SignupSession | null> => {
    // The session event normally arrives before the login callback; allow a short grace period.
    for (let i = 0; i < 40 && !session.current; i++) {
      await new Promise((r) => setTimeout(r, 250));
    }
    return session.current;
  };

  const start = async () => {
    setError(null);
    setBusy(true);
    session.current = null;
    cancelled.current = false;
    try {
      const fb = await loadSdk(appId, graphVersion);
      fb.login(
        (response) => {
          void (async () => {
            try {
              const code = response.authResponse?.code;
              if (!code) {
                setError(
                  cancelled.current
                    ? 'Signup was cancelled.'
                    : 'Signup was not completed, so nothing was connected.',
                );
                return;
              }
              const found = await waitForSession();
              if (!found) {
                setError('Meta did not report which account was created. Please try again.');
                return;
              }
              await api.post('/whatsapp/embedded-signup', { code, wabaId: found.wabaId });
              toast.success('WhatsApp account connected');
              onConnected();
            } catch (err) {
              setError(errorMessage(err));
            } finally {
              setBusy(false);
            }
          })();
        },
        {
          config_id: configId,
          response_type: 'code',
          override_default_response_type: true,
          extras: {
            setup: solutionId ? { solutionID: solutionId } : {},
            featureType: 'whatsapp_business_app_onboarding',
            sessionInfoVersion: '3',
            features: [{ name: 'marketing_messages_lite' }],
            version: 'v3',
          },
        },
      );
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  return (
    <div className="stack">
      {error && <Alert tone="critical">{error}</Alert>}
      <p className="text-muted">
        Opens Meta&apos;s secure signup window. Choose or create a WhatsApp Business Account and a
        phone number; Cartly360 never sees your Facebook password.
      </p>
      <div>
        <Button
          variant="primary"
          icon={<MessageCircle size={14} />}
          loading={busy}
          onClick={() => void start()}
        >
          Connect with Facebook
        </Button>
      </div>
    </div>
  );
}
